import { useMemo } from 'react'
import { TruncatedText } from '@/components/commons/truncated-text'
import { DataTable } from '@/components/ui/data-table'
import { Skeleton } from '@/components/ui/skeleton'
import { LocationRowMenu } from '@/features/marquee/components/location-row-menu'
import { LocationStatusBadge } from '@/features/marquee/components/location-status-badge'
import { TableScrollBox } from '@/features/marquee/components/table-scroll-box'
import {
	TableEmpty,
	TableError
} from '@/features/marquee/components/table-states'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import {
	type LocationRowAction,
	locationStatus,
	timeZoneOffsetLabel
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeLocation } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5']

/** Deactivated rows read muted (OPSSTD-10100); the status pill keeps its colour. */
const cellTextClass = 'font-medium text-sm/4.5 text-grey-1000'

interface LocationsTableProps {
	data: MarqueeLocation[]
	isLoading?: boolean
	isError?: boolean
	onAction: (action: LocationRowAction, location: MarqueeLocation) => void
}

/**
 * The Locations list: one row per custom location, already filtered and
 * sorted by the page.
 *
 * Desktop renders the virtualized `DataTable` inside the card's scroll box;
 * below `l` every row becomes a card, the same split the Team table makes.
 */
export function LocationsTable({
	data,
	isLoading,
	isError,
	onAction
}: LocationsTableProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')

	const columns = useMemo(
		() => [
			{
				id: 'location' as const,
				header: t('marquee:locations_col_location'),
				cell: ({ dataItem }: { dataItem: MarqueeLocation }) => (
					<TruncatedText className={cellTextClass}>
						{dataItem.name}
					</TruncatedText>
				),
				size: 260
			},
			{
				id: 'parentMarket' as const,
				header: t('marquee:locations_col_parent_market'),
				cell: ({ dataItem }: { dataItem: MarqueeLocation }) => (
					<TruncatedText className={cellTextClass}>
						{dataItem.market.name}
					</TruncatedText>
				),
				size: 200
			},
			{
				id: 'timeZone' as const,
				header: t('marquee:locations_col_time_zone'),
				cell: ({ dataItem }: { dataItem: MarqueeLocation }) => (
					<TimeZoneCell location={dataItem} />
				),
				size: 180
			},
			{
				id: 'events' as const,
				header: t('marquee:locations_col_events'),
				cell: ({ dataItem }: { dataItem: MarqueeLocation }) => (
					<span className={cn(cellTextClass, 'tabular-nums')}>
						{dataItem.event_count}
					</span>
				),
				size: 160
			},
			{
				id: 'status' as const,
				header: t('marquee:locations_col_status'),
				cell: ({ dataItem }: { dataItem: MarqueeLocation }) => (
					<LocationStatusBadge status={locationStatus(dataItem)} />
				),
				size: 160
			},
			{
				id: 'actions' as const,
				header: null,
				headerAlign: 'end' as const,
				cell: ({ dataItem }: { dataItem: MarqueeLocation }) => (
					<div className='flex w-full items-center justify-end'>
						<LocationRowMenu
							location={dataItem}
							onAction={onAction}
						/>
					</div>
				),
				size: 72
			}
		],
		[t, onAction]
	)

	if (isError) {
		return (
			<TableError
				description='marquee:locations_error_description'
				title='marquee:locations_error_title'
			/>
		)
	}

	if (!isDesktop) {
		if (isLoading) {
			return (
				<div className='flex flex-col gap-3'>
					{SKELETON_KEYS.map((key) => (
						<Skeleton height={140} key={key} />
					))}
				</div>
			)
		}
		if (data.length === 0) {
			return (
				<TableEmpty
					description='marquee:locations_empty_description'
					title='marquee:locations_empty_title'
				/>
			)
		}
		return (
			<div className='flex flex-col gap-3'>
				{data.map((location) => (
					<LocationCard
						key={location.id}
						location={location}
						onAction={onAction}
					/>
				))}
			</div>
		)
	}

	return (
		<TableScrollBox>
			{(scrollParent) => (
				<div className='**:data-[slot=table-header-gap-row]:h-px **:data-[slot=table-header-gap-row]:bg-grey-300'>
					<DataTable
						columnPinning={{ left: [], right: ['actions'] }}
						columns={columns}
						customScrollParent={scrollParent}
						data={data}
						emptyComponent={
							<TableEmpty
								description='marquee:locations_empty_description'
								title='marquee:locations_empty_title'
							/>
						}
						isLoading={isLoading}
					/>
				</div>
			)}
		</TableScrollBox>
	)
}

/**
 * The offset as the design shows it ("GMT-4"), with the IANA name kept on
 * hover: several zones share an offset, and the name is what was saved.
 */
function TimeZoneCell({ location }: { location: MarqueeLocation }) {
	return (
		<span className={cellTextClass} title={location.time_zone}>
			{timeZoneOffsetLabel(location.time_zone)}
		</span>
	)
}

function LocationCard({
	location,
	onAction
}: {
	location: MarqueeLocation
	onAction: (action: LocationRowAction, location: MarqueeLocation) => void
}) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-col gap-3 rounded-2xl bg-neutral-white p-4 shadow-airbnb-3'>
			<div className='flex items-start justify-between gap-3'>
				<TruncatedText className={cellTextClass}>
					{location.name}
				</TruncatedText>
				<LocationRowMenu location={location} onAction={onAction} />
			</div>
			<dl className='flex flex-col gap-1.5 text-sm'>
				<CardRow label={t('marquee:locations_col_parent_market')}>
					{location.market.name}
				</CardRow>
				<CardRow label={t('marquee:locations_col_time_zone')}>
					<TimeZoneCell location={location} />
				</CardRow>
				<CardRow label={t('marquee:locations_col_events')}>
					{location.event_count}
				</CardRow>
				<CardRow label={t('marquee:locations_col_status')}>
					<LocationStatusBadge status={locationStatus(location)} />
				</CardRow>
			</dl>
		</div>
	)
}

function CardRow({
	label,
	children
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div className='flex items-center justify-between gap-4'>
			<dt className='shrink-0 text-neutral-500'>{label}</dt>
			<dd className='wrap-break-word flex min-w-0 justify-end text-right text-neutral-900'>
				{children}
			</dd>
		</div>
	)
}
